import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import SEO from '../../components/SEO';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumb from '../../components/Breadcrumb';
import InternalLinks from '../../components/InternalLinks';
import { 
  FileText,
  ArrowLeft,
  Clock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Lightbulb,
  FileCheck,
  Scale,
  Briefcase,
  Shield
} from 'lucide-react';

export default function RCvsCCTPvsCCAPArticle() {
  return (
    <>
      <SEO 
        title="RC vs CCTP vs CCAP : Comprendre les Pièces du DCE | Guide 2026"
        description="Différences entre RC, CCTP et CCAP dans un DCE. Règlement de consultation, cahier des clauses techniques et administratives. Guide complet pour les marchés publics."
        path="/blog/rc-vs-cctp-vs-ccap"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-slate-50 border-b">
          <div className="container mx-auto px-4 py-3">
            <Breadcrumb 
              items={[
                { label: 'Blog', href: '/blog' },
                { label: 'RC vs CCTP vs CCAP' }
              ]}
            />
          </div>
        </div>

        {/* Article Header */}
        <article>
          <header className="bg-gradient-to-b from-violet-50 to-white py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                  Guide DCE
                </span>
                <span className="flex items-center gap-1 text-slate-500 text-sm">
                  <Calendar className="w-4 h-4" />
                  12 mars 2026
                </span>
                <span className="flex items-center gap-1 text-slate-500 text-sm">
                  <Clock className="w-4 h-4" />
                  12 min de lecture
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                RC vs CCTP vs CCAP : Comprendre les Pièces du DCE
              </h1>
              
              <p className="text-xl text-slate-600">
                RC, CCTP, CCAP, AE, BPU... Ces acronymes peuvent paraître complexes. Ce guide vous explique le rôle de chaque document du DCE et comment les analyser efficacement.
              </p>
            </div>
          </header>

          {/* Article Content */}
          <div className="container mx-auto px-4 max-w-4xl py-12">
            <div className="prose prose-lg max-w-none">
              
              <p>
                Lorsque vous consultez un <Link to="/analyse-dce" className="text-violet-600 hover:text-violet-700 font-medium">DCE (Dossier de Consultation des Entreprises)</Link>, vous êtes confronté à plusieurs documents aux noms techniques. Comprendre le rôle de chacun est essentiel pour analyser efficacement un appel d'offres.
              </p>
              
              <p>
                Avant d'analyser ces documents, il est recommandé de mettre en place une <Link to="/veille-marches-publics" className="text-violet-600 hover:text-violet-700 font-medium">veille marchés publics</Link> pour identifier les appels d'offres pertinents, puis d'utiliser une <Link to="/plateforme-appels-offres" className="text-violet-600 hover:text-violet-700 font-medium">plateforme spécialisée</Link> pour centraliser vos recherches.
              </p>

              {/* Quick Reference Card */}
              <Card className="my-8 border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-violet-600" />
                    En bref : les 3 documents clés
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-violet-100">
                      <div className="font-bold text-violet-700 mb-1">RC</div>
                      <div className="text-sm text-slate-600">Règlement de Consultation</div>
                      <div className="text-xs text-slate-500 mt-2">Comment répondre ?</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-orange-100">
                      <div className="font-bold text-orange-700 mb-1">CCAP</div>
                      <div className="text-sm text-slate-600">Clauses Administratives</div>
                      <div className="text-xs text-slate-500 mt-2">Conditions du contrat ?</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-green-100">
                      <div className="font-bold text-green-700 mb-1">CCTP</div>
                      <div className="text-sm text-slate-600">Clauses Techniques</div>
                      <div className="text-xs text-slate-500 mt-2">Que faut-il faire ?</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6" id="rc">
                Le RC : Règlement de Consultation
              </h2>

              <p>
                Le <strong>Règlement de Consultation (RC)</strong> est votre guide de procédure. C'est le document qui vous explique <em>comment</em> répondre à l'appel d'offres.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">
                Que contient le RC ?
              </h3>

              <ul className="space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span><strong>L'objet du marché</strong> : description synthétique de la prestation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span><strong>La procédure</strong> : appel d'offres ouvert, restreint, MAPA...</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span><strong>Les critères d'attribution</strong> et leur pondération</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span><strong>Les pièces à fournir</strong> : candidature et offre</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span><strong>La date limite</strong> de remise des offres</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span><strong>Les variantes</strong> : autorisées ou non</span>
                </li>
              </ul>

              <Card className="my-8 border-amber-200 bg-amber-50">
                <CardContent className="p-6">
                  <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Conseil d'expert
                  </h4>
                  <p className="text-amber-900">
                    Le RC est le premier document à lire. C'est lui qui vous donnera les <strong>critères de notation</strong>. Si le prix compte pour 70%, concentrez vos efforts sur l'optimisation tarifaire. Si la valeur technique compte pour 60%, investissez dans votre mémoire technique.
                  </p>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6" id="ccap">
                Le CCAP : Cahier des Clauses Administratives Particulières
              </h2>

              <p>
                Le <strong>CCAP</strong> définit les conditions contractuelles du marché. C'est le document qui régit la relation entre l'acheteur public et le titulaire du marché.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">
                Les éléments clés du CCAP
              </h3>

              <div className="grid md:grid-cols-2 gap-4 my-6">
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Scale className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold">Conditions financières</span>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Modalités de paiement</li>
                      <li>• Délais de règlement</li>
                      <li>• Avances et acomptes</li>
                      <li>• Révision des prix</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <span className="font-semibold">Pénalités et garanties</span>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Pénalités de retard</li>
                      <li>• Retenue de garantie</li>
                      <li>• Assurances requises</li>
                      <li>• Garantie de parfait achèvement</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-green-600" />
                      <span className="font-semibold">Délais</span>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Durée du marché</li>
                      <li>• Délai d'exécution</li>
                      <li>• Reconductions possibles</li>
                      <li>• Délai de notification</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold">Responsabilités</span>
                    </div>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Responsabilité civile</li>
                      <li>• Propriété intellectuelle</li>
                      <li>• Confidentialité</li>
                      <li>• Résiliation</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <Card className="my-8 border-red-200 bg-red-50">
                <CardContent className="p-6">
                  <h4 className="font-bold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Point de vigilance
                  </h4>
                  <p className="text-red-900">
                    Les <strong>pénalités de retard</strong> peuvent être très élevées dans les marchés publics (souvent 1/1000e du montant par jour de retard). Analysez attentivement cette clause avant de vous engager.
                  </p>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6" id="cctp">
                Le CCTP : Cahier des Clauses Techniques Particulières
              </h2>

              <p>
                Le <strong>CCTP</strong> décrit précisément <em>ce que</em> vous devez réaliser. C'est le document technique de référence qui définit les spécifications du marché.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">
                Contenu type d'un CCTP
              </h3>

              <ul className="space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <FileCheck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                  <span><strong>Description technique</strong> des prestations à réaliser</span>
                </li>
                <li className="flex items-start gap-3">
                  <FileCheck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                  <span><strong>Normes et standards</strong> à respecter</span>
                </li>
                <li className="flex items-start gap-3">
                  <FileCheck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                  <span><strong>Livrables attendus</strong> et format de remise</span>
                </li>
                <li className="flex items-start gap-3">
                  <FileCheck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                  <span><strong>Exigences de qualité</strong> et critères d'acceptation</span>
                </li>
                <li className="flex items-start gap-3">
                  <FileCheck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                  <span><strong>Contraintes techniques</strong> (environnement, interopérabilité...)</span>
                </li>
              </ul>

              <p>
                Le CCTP est essentiel pour rédiger votre <Link to="/blog/comment-rediger-memoire-technique" className="text-violet-600 hover:text-violet-700 font-medium">mémoire technique</Link>. Chaque exigence du CCTP doit trouver une réponse dans votre offre.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Les autres documents du DCE
              </h2>

              <p>
                En plus des trois documents principaux, un DCE peut contenir d'autres pièces :
              </p>

              <div className="overflow-x-auto my-8">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border border-slate-300 px-4 py-2 text-left">Acronyme</th>
                      <th className="border border-slate-300 px-4 py-2 text-left">Nom complet</th>
                      <th className="border border-slate-300 px-4 py-2 text-left">Rôle</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-300 px-4 py-2 font-medium">AE</td>
                      <td className="border border-slate-300 px-4 py-2">Acte d'Engagement</td>
                      <td className="border border-slate-300 px-4 py-2">Document contractuel à signer</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-2 font-medium">BPU</td>
                      <td className="border border-slate-300 px-4 py-2">Bordereau des Prix Unitaires</td>
                      <td className="border border-slate-300 px-4 py-2">Liste des prix par prestation</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-2 font-medium">DQE</td>
                      <td className="border border-slate-300 px-4 py-2">Détail Quantitatif Estimatif</td>
                      <td className="border border-slate-300 px-4 py-2">Estimation des quantités</td>
                    </tr>
                    <tr className="bg-slate-50">
                      <td className="border border-slate-300 px-4 py-2 font-medium">DPGF</td>
                      <td className="border border-slate-300 px-4 py-2">Décomposition du Prix Global et Forfaitaire</td>
                      <td className="border border-slate-300 px-4 py-2">Détail du prix forfaitaire</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-300 px-4 py-2 font-medium">CCAG</td>
                      <td className="border border-slate-300 px-4 py-2">Cahier des Clauses Administratives Générales</td>
                      <td className="border border-slate-300 px-4 py-2">Conditions générales par type de marché</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Ordre de lecture recommandé
              </h2>

              <p>
                Pour analyser efficacement un DCE, voici l'ordre de lecture optimal :
              </p>

              <ol className="space-y-4 my-6">
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                  <div>
                    <strong className="text-slate-900">RC (Règlement de Consultation)</strong>
                    <p className="text-slate-600 text-sm mt-1">Comprendre la procédure, les critères et les pièces à fournir</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                  <div>
                    <strong className="text-slate-900">CCAP (Clauses Administratives)</strong>
                    <p className="text-slate-600 text-sm mt-1">Évaluer les risques contractuels et les conditions financières</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                  <div>
                    <strong className="text-slate-900">CCTP (Clauses Techniques)</strong>
                    <p className="text-slate-600 text-sm mt-1">Analyser les exigences techniques et vérifier votre capacité à répondre</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">4</span>
                  <div>
                    <strong className="text-slate-900">BPU/DQE ou DPGF</strong>
                    <p className="text-slate-600 text-sm mt-1">Construire votre offre de prix</p>
                  </div>
                </li>
              </ol>

              <Card className="my-8 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">
                    Gagnez du temps avec l'analyse IA
                  </h3>
                  <p className="mb-4 text-violet-100">
                    Notre outil d'analyse DCE par IA extrait automatiquement les informations clés de chaque document en quelques secondes : critères, délais, pénalités, exigences techniques.
                  </p>
                  <Link to="/analyse-dce">
                    <Button className="bg-white text-violet-700 hover:bg-violet-50">
                      Essayer l'analyse IA gratuite
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                Conclusion
              </h2>

              <p>
                La maîtrise des documents du DCE est fondamentale pour réussir dans les marchés publics. Le RC vous guide sur la procédure, le CCAP fixe les règles du contrat, et le CCTP définit ce que vous devez réaliser.
              </p>

              <p>
                En comprenant le rôle de chaque document, vous pourrez :
              </p>

              <ul className="space-y-2 my-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>Analyser plus rapidement les appels d'offres</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>Identifier les risques avant de vous engager</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span>Préparer des offres conformes et compétitives</span>
                </li>
              </ul>

            </div>

            {/* Aller plus loin - Pillar Pages */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Aller plus loin</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/veille-marches-publics" className="group p-4 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors">
                  <span className="font-semibold text-violet-700 group-hover:text-violet-800">Découvrir la veille marchés publics</span>
                  <p className="text-sm text-slate-600 mt-1">Surveillez les opportunités adaptées à votre entreprise</p>
                </Link>
                <Link to="/trouver-appels-offres" className="group p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
                  <span className="font-semibold text-indigo-700 group-hover:text-indigo-800">Apprendre à trouver des appels d'offres</span>
                  <p className="text-sm text-slate-600 mt-1">Sources, méthodes et bonnes pratiques</p>
                </Link>
                <Link to="/analyse-dce" className="group p-4 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                  <span className="font-semibold text-emerald-700 group-hover:text-emerald-800">Comprendre l'analyse d'un DCE</span>
                  <p className="text-sm text-slate-600 mt-1">Méthode IA pour extraire les informations clés</p>
                </Link>
                <Link to="/plateforme-appels-offres" className="group p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                  <span className="font-semibold text-orange-700 group-hover:text-orange-800">Découvrir la plateforme DCE Express</span>
                  <p className="text-sm text-slate-600 mt-1">Toutes les fonctionnalités pour répondre aux marchés</p>
                </Link>
              </div>
            </div>

            {/* Internal Links */}
            <InternalLinks 
              links={[
                { label: 'Comment analyser un DCE', href: '/blog/comment-analyser-dce' },
                { label: 'Comment rédiger un mémoire technique', href: '/blog/comment-rediger-memoire-technique' },
                { label: 'Comment répondre à un appel d\'offres', href: '/blog/comment-repondre-appel-offres' },
                { label: 'Analyse DCE par IA', href: '/analyse-dce' }
              ]}
            />

            {/* Author */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">L'équipe Mon DCE Express</p>
                  <p className="text-sm text-slate-500">Experts en marchés publics et analyse DCE</p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
}
